// src/components/ERDEditor/SaveIndicator.jsx
import React from 'react';
import {
  MdSave,
  MdError,
  MdHourglassEmpty,
  MdCloudDone,
  MdCloudOff,
  MdBlockFlipped
} from 'react-icons/md';

const SaveIndicator = ({ 
  saveStatus, 
  lastSaved, 
  error,
  environment,
  className = "" 
}) => {
  // Format last saved time
  const formatLastSaved = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const saved = new Date(timestamp);
    const diffInSeconds = Math.floor((now - saved) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      return saved.toLocaleDateString();
    }
  };

  // Get status display configuration
  const getStatusConfig = () => {
    // Special case for 'None' environment - override all other statuses
    if (environment === 'None') {
      return {
        icon: MdBlockFlipped,
        text: 'No Saves',
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-700',
        iconColor: 'text-orange-500',
        borderColor: 'border-orange-200'
      };
    }

    switch (saveStatus) {
      case 'saved':
        return {
          icon: MdCloudDone,
          text: 'Saved',
          bgColor: 'bg-green-50',
          textColor: 'text-green-700',
          iconColor: 'text-green-500',
          borderColor: 'border-green-200'
        };
      case 'saving':
        return {
          icon: MdSave,
          text: 'Saving...',
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-700',
          iconColor: 'text-blue-500',
          borderColor: 'border-blue-200',
          animate: true
        };
      case 'pending':
        return {
          icon: MdHourglassEmpty,
          text: 'Pending',
          bgColor: 'bg-yellow-50',
          textColor: 'text-yellow-700',
          iconColor: 'text-yellow-500',
          borderColor: 'border-yellow-200',
          animate: true
        };
      case 'error':
        return {
          icon: MdCloudOff,
          text: 'Error',
          bgColor: 'bg-red-50',
          textColor: 'text-red-700',
          iconColor: 'text-red-500',
          borderColor: 'border-red-200'
        };
      case 'unsaved':
      default:
        return {
          icon: MdError,
          text: 'Unsaved',
          bgColor: 'bg-gray-50',
          textColor: 'text-gray-700',
          iconColor: 'text-gray-500',
          borderColor: 'border-gray-200'
        };
    }
  };

  const config = getStatusConfig();
  const IconComponent = config.icon;

  return (
    <div className={`flex items-center space-x-2 px-3 py-2 rounded-md border ${config.bgColor} ${config.borderColor} ${className}`}>
      <IconComponent 
        className={`w-4 h-4 ${config.iconColor} ${config.animate ? 'animate-pulse' : ''}`} 
      />
      <div className="flex flex-col">
        <span className={`text-xs font-medium ${config.textColor}`}>
          {config.text}
        </span>
        {lastSaved && saveStatus !== 'error' && environment !== 'None' && (
          <span className="text-xs text-gray-500">
            {formatLastSaved(lastSaved)}
          </span>
        )}
        {environment === 'None' && (
          <span className="text-xs text-orange-500">
            Environment: None
          </span>
        )}
        {error && saveStatus === 'error' && (
          <span className="text-xs text-red-500" title={error}>
            {error.length > 20 ? `${error.substring(0, 20)}...` : error}
          </span>
        )}
      </div>
    </div>
  );
};

export default SaveIndicator;